/**
 * ドメイン設定の「端末証明書のインストールメール」。OS × 言語で件名と本文を持つ。
 *
 * `DomainPage` の `<Form>` の中に置いて使う。値は `device_cert_mail.<os>.<lang>.{subject,body}`
 * に直接ぶら下げるので、保存は既存の PUT `/api/idp/domain` にそのまま乗る。
 *
 * タブは `forceRender` で全部作る。**開いていないタブの入力も一緒に保存したい**ため
 * (antd の Form は未マウントの値も保持するが、初回描画前だと取りこぼしうる)。
 *
 * 文面に p12 のパスワードは入れない。差し込めるのは `DEVICE_CERT_MAIL_PLACEHOLDERS` だけで、
 * そこにパスワードは無い(URL だけを送り、パスワードは別の手段で渡す運用)。
 */
import { Alert, Button, Card, Descriptions, Form, Input, Space, Tabs, Typography } from "antd";
import { useState } from "react";

import type { DeviceCertLang, DeviceCertOs } from "../../lib/api";
import { useDeviceCertMailPreview } from "./deviceCertHooks";
import {
  DEVICE_CERT_LANGS,
  DEVICE_CERT_LANG_LABEL,
  DEVICE_CERT_MAIL_PLACEHOLDERS,
  DEVICE_CERT_OSES,
  DEVICE_CERT_OS_LABEL,
} from "./deviceCertOptions";
import { errorText } from "./hooks";
import { UserPicker } from "./UserPicker";

const { Text, Paragraph } = Typography;

/** 1 言語ぶんの件名・本文。名前は `device_cert_mail.<os>.<lang>.…` に固定。 */
function MailFields({ os, lang }: { os: DeviceCertOs; lang: DeviceCertLang }) {
  return (
    <>
      <Form.Item
        name={["device_cert_mail", os, lang, "subject"]}
        label="件名"
        rules={[{ max: 200, message: "件名は 200 文字まで" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name={["device_cert_mail", os, lang, "body"]} label="本文">
        <Input.TextArea rows={10} />
      </Form.Item>
    </>
  );
}

export function DeviceCertMailCard({ canEdit }: { canEdit: boolean }) {
  const [os, setOs] = useState<DeviceCertOs>("macos");
  const [lang, setLang] = useState<DeviceCertLang>("ja");
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [previewing, setPreviewing] = useState(false);
  const preview = useDeviceCertMailPreview({ os, lang, userId }, previewing);

  return (
    <Card size="small" title="端末証明書のインストールメール">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="メールに p12 のパスワードは入らない"
        description="送れるのはダウンロード URL だけ。パスワードは発行のときに画面へ 1 回だけ出るので、そこで控えて本人に別の手段で渡す。"
      />

      {/* 差し込みの一覧はタブの外(どのタブを開いていても見える) */}
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        本文と件名に差し込めるもの:{" "}
        {DEVICE_CERT_MAIL_PLACEHOLDERS.map((p) => (
          <Text key={p.key} code style={{ marginRight: 6 }}>
            {p.key}
          </Text>
        ))}
        <br />
        {DEVICE_CERT_MAIL_PLACEHOLDERS.map((p) => `${p.key} = ${p.label}`).join(" / ")}
      </Paragraph>

      <Tabs
        activeKey={os}
        onChange={(k) => setOs(k as DeviceCertOs)}
        items={DEVICE_CERT_OSES.map((o) => ({
          key: o,
          label: DEVICE_CERT_OS_LABEL[o],
          forceRender: true,
          children: (
            <Tabs
              size="small"
              activeKey={lang}
              onChange={(k) => setLang(k as DeviceCertLang)}
              items={DEVICE_CERT_LANGS.map((l) => ({
                key: l,
                label: DEVICE_CERT_LANG_LABEL[l],
                forceRender: true,
                children: <MailFields os={o} lang={l} />,
              }))}
            />
          ),
        }))}
      />

      <Space direction="vertical" style={{ width: "100%" }}>
        <Space wrap align="center">
          <Text type="secondary">プレビュー({DEVICE_CERT_OS_LABEL[os]} /{" "}
            {DEVICE_CERT_LANG_LABEL[lang]}):
          </Text>
          <UserPicker
            value={userId}
            onChange={setUserId}
            allowClear
            placeholder="差し込みに使うユーザー(省略可)"
            style={{ width: 280 }}
          />
          <Button
            onClick={() => {
              setPreviewing(true);
              void preview.refetch();
            }}
            loading={preview.isFetching}
          >
            保存済みの文面で試す
          </Button>
        </Space>
        <Text type="secondary">
          プレビューは「保存済み」の文面で作る。直した文面を見たいときは先に保存する。
        </Text>

        {previewing && preview.isError && (
          <Alert
            type="error"
            showIcon
            message="プレビューを作れなかった"
            description={errorText(preview.error)}
          />
        )}
        {previewing && preview.data && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="件名">{preview.data.subject}</Descriptions.Item>
            <Descriptions.Item label="本文">
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                {preview.data.body}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>

      {!canEdit && <Text type="secondary">直すには idp:admin が要る</Text>}
    </Card>
  );
}
